<template>
  <cs-preloader-overlay :loading="loading">
    <div :class="$style.container">
      <div :class="$style.content">

        <div :class="$style.poster">
          <residency-poster slot="poster" />
        </div>

        <div :class="$style.main">
          <div :class="$style.header">
            <screen-header
              :title="$t('registration.currency.title')"
              :is-desktop="isDesktop"
              :history-mode="false"
              @navigate="$router.push({ name: 'projects' }).catch(() => {})"
            >
              <localization-controller
                slot="toolbar"
                :language="language"
                inverted-color
              />
            </screen-header>
          </div>

          <div v-if="$validate.isNotEmptyArray(currencies)" :class="$style.form">
            <div v-scrollbar="{ id: `scrollbar-${'scrollbarId'}` }" :class="$style.list">
              <scrollbar-vertical
                :id="`scrollbar-${'scrollbarId'}`"
                :class="$style.scrollbar"
              />
              <collapsible-list :active-idx="selectedIdx" :language="language">
                <currency-panel
                  v-for="(currency, idx) in formattedCurrencies"
                  :key="idx"
                  :data-test="`projectCurrency${currency.code}`"
                  :currency="currency"
                  @click="handleSelect(idx)"
                />
              </collapsible-list>
            </div>
          </div>

          <div v-if="isShownFooter" :class="$style.footer">
            <div v-if="isUzCurrency" :class="$style.message">
              <p>{{ $t('registration.currency.additional.title') }}</p>
              <ul>
                <li>{{ $t('registration.currency.additional.uz.one') }}</li>
              </ul>
              <p>{{ $t('registration.currency.additional.message') }}</p>
            </div>

            <div :class="$style.policy" v-html="displayPolicyByCurrency"></div>

            <div :class="$style.submit">
              <simple-button variant="secondary" @click="handleSubmit">
                {{ $t('registration.currency.submit') }}
              </simple-button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { getCurrencies } from 'services/currency';
import { getLocaleCode } from '@/services/i18n-setup';

// import HTTPService from '@/services/Http';
// import logrocket from '@/services/logrocket';

import { EVENT_BUS } from '@/eventBus';

import SimpleButton from 'molecules/common/SimpleButton';
import CollapsibleList from 'molecules/common/CollapsibleList';
import ResidencyPoster from 'molecules/auth/ResidencyPoster';
import CurrencyPanel from 'molecules/currency/CurrencyPanel';
import { ScrollbarDirective, ScrollbarVertical } from 'molecules/scrollbar';

import ScreenHeader from 'molecules/common/ScreenHeader';
import LocalizationController from 'organisms/localization/LocalizationController';

const TEXT_POLICY_MAP = {
  ['RUB']: 'https://docs.capusta.space/Agreement.pdf',
  ['UZS']: 'https://docs.capusta.space/Agreement-uz.pdf',
};

export default {
  name: 'ProjectCurrency',
  components: {
    SimpleButton,
    CurrencyPanel,
    CollapsibleList,
    ResidencyPoster,
    ScrollbarVertical,
    ScreenHeader,
    LocalizationController,
  },
  props: {
    destination: {
      type: String,
    },
    projectLink: {
      type: String,
    },
  },
  directives: {
    'scrollbar': ScrollbarDirective,
  },
  created() {
    this.loadCurrencies();

    EVENT_BUS.$on('change.localization', language => {
      this.language = language;
    });
  },
  data() {
    return {
      loading: false,
      currencies: [],
      selectedIdx: null,
      language: {
        code: getLocaleCode().toUpperCase(),
      },
    };
  },
  computed: {
    isShownFooter() {
      return this.$validate.isNumber(this.selectedIdx);
    },
    formattedCurrencies() {
      const formattedCurrencies = this.currencies.map(currency => {
        return {
          ...currency,
          active: false,
          description: '',
        };
      });

      return formattedCurrencies;
    },
    currency() {
      if (this.$validate.isNull(this.selectedIdx)) {
        return null;
      }

      return this.currencies[this.selectedIdx];
    },
    displayPolicyByCurrency() {
      if (this.$validate.isNull(this.currency)) {
        return '';
      }

      return this.$t('registration.currency.agreement', {
        link: TEXT_POLICY_MAP[this.currency.code],
        dataTestAgreement: 'data-test="projectCurrencyAgreementLink"',
      });
    },
    isUzCurrency() {
      if (this.$validate.isNull(this.currency)) {
        return false;
      }

      return this.currency.code === 'UZS';
    },
    ...mapGetters(['isDesktop', 'getCurrentProject']),
  },
  methods: {
    async loadCurrencies() {
      this.loading = true;

      try {
        const data = await getCurrencies();
        this.currencies = data;
      } catch (error) {
        console.error(error);
      }

      this.loading = false;
    },
    handleSelect(currencyIdx) {
      if (this.$validate.isNull(this.selectedIdx)) {
        this.currencies[currencyIdx].description = this.$t('project.currency');
        this.selectedIdx = currencyIdx;
        return;
      }

      this.currencies[this.selectedIdx].description = '';
      this.selectedIdx = null;
    },
    async handleSubmit() {
      try {
        const project = await this.getProject();

        await this.updateProject({
          ...project,
          currency: this.currency,
        });

        this.$alert({
          type: 'success',
          text: 'common.success',
        });

        this.$router.push({ name: 'cabinet' });
      } catch (error) {
        console.log(error);

        this.$alert({
          type: 'warning',
          text: 'project.currency.error',
        });
      }
    },
    ...mapActions('project', ['getProject', 'updateProject']),
  },
};
</script>

<style lang="sass" module>
  .container
    width: 100%
    min-height: 100vh
    position: relative
    padding: 20px 0px 40px
    overflow: hidden

    +respond-to('medium')
      display: flex
      align-items: center

  .main
    width: 100%
    max-width: 460px
    position: relative
    margin: 0 auto

    +respond-to('medium')
      width: 460px
      margin-left: 90px

    &:after
      +size(614px)
      content: ''
      position: absolute
      top: -260px
      right: -210px
      border-radius: 50%
      background: linear-gradient(180deg, #246298 0%, #1E504B 100%)
      transform: rotate(-90deg)
      z-index: -1

      +respond-to('medium')
        width: 2200px
        height: 2200px
        top: 50%
        right: 0
        bottom auto
        left: -90px
        transform: translateY(-50%) rotate(180deg)
        background: linear-gradient(180deg, #246298 0%, #1E504B 100%)

  .header
    display: flex
    align-items: center
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('medium')
      margin-left: -80px
      margin-bottom: 40px

  .navigate
    &.mobile
      +flexbox
      margin-bottom: 4px

      svg
        +size(18px)

    &.desktop
      display: none

    +respond-to(medium)
      &.mobile
        display: none

      &.desktop
        +flexbox
        margin-left: -80px
        padding-right: 20px

  .content
    margin: 0 auto
    display: flex

  .poster
    display: none

    +respond-to('medium')
      display: flex
      align-items: center

  .form
    +base-sub-mount
    margin-bottom: 20px
    padding: 20px 0

  .list
    max-height: 560px
    position: relative
    padding: 0 20px

  .footer
    padding: 0 20px

  .message
    margin-bottom: 20px
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: var(--color-white)
    opacity: .75

    ul
      list-style: disc
      margin-left: 20px

      li
        margin-bottom: 10px

    p
      &:not(:last-child)
        margin-bottom: 10px

  .policy
    margin-bottom: 20px
    font-weight: 400
    font-size: 12px
    line-height: 17px
    color: var(--color-white)

    a
      text-decoration: underline

  .submit

  .scrollbar
    height: 100%
    position: absolute
    top: 0
    right: 5px
</style>
